import PrintIcon from '@mui/icons-material/Print'
import { Box, Button, Divider, Stack, Typography } from '@mui/material'
import React, { useCallback, useMemo } from 'react'

import useAccessTokenAndShopId from '../../../hooks/useAccessTokenAndShopId/useAccessTokenAndShopId'
import {
  useArchiveCounterSaleMutation,
  useUpdateCounterSaleMutation
} from '../../../redux/api'
import { CounterSaleExpanded, CustomerEntry } from '../../../sdk'
import { formatCurrency } from '../../../utils'
import { CustomerLookupInput } from '../../CustomerLookUp'
import CounterSaleCartDiscount from './CounterSaleCartDiscount'
import CounterSaleCartInventory from './CounterSaleCartInventory'
import CounterSaleCartProduct from './CounterSaleCartProduct'
import CounterSaleEmptyCart from './CounterSaleEmptyCart'

interface Props {
  counterSale: CounterSaleExpanded | undefined
  handleNext: () => void
  handleFinish: () => void
  showCheckout: boolean
  showReceipt: boolean
  showCustomer: boolean
  readOnly: boolean
}

const CounterSaleCart = ({
  counterSale,
  handleNext,
  handleFinish,
  showCheckout,
  showReceipt,
  showCustomer,
  readOnly
}: Props) => {
  const { accessToken, shopId } = useAccessTokenAndShopId()
  const [archiveCounterSale] = useArchiveCounterSaleMutation()
  const [updateCounterSale] = useUpdateCounterSaleMutation()

  const handleCustomerChange = useCallback(
    (customer: CustomerEntry | undefined) => {
      updateCounterSale({
        authorization: accessToken,
        counterSaleEntry: {
          ...counterSale?.entry,
          info: {
            ...counterSale?.entry?.info,
            customer: customer ? customer.id : undefined
          }
        }
      })
    },
    [accessToken, counterSale?.entry, updateCounterSale]
  )

  const removeAllParts = useCallback(() => {
    if (!counterSale?.entry?.id) {
      return
    }
    if (counterSale.pricing?.payments?.length === 0) {
      archiveCounterSale({
        authorization: accessToken,
        shop: shopId,
        id: counterSale.entry.id,
        archiveReason: 'Clear parts from cart'
      })
    } else {
      handleFinish()
    }
  }, [
    accessToken,
    counterSale?.entry?.id,
    archiveCounterSale,
    shopId,
    counterSale?.pricing?.payments?.length,
    handleFinish
  ])

  const numItemsInCart = useMemo(() => {
    if (!counterSale) {
      return 0
    }
    const numInventoryParts =
      counterSale.pricing?.inventoryPartEntries?.length || 0
    const numProducts =
      counterSale.pricing?.cannedJobCustomPartEntries?.length || 0
    return numInventoryParts + numProducts
  }, [counterSale])

  const openReceipt = () => {
    window.open(`/print.html?id=${counterSale?.entry?.id}&type=counterSale`)
  }

  if (!counterSale) {
    return <CounterSaleEmptyCart />
  }

  return (
    <>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="h1">
          {showReceipt ? `Order #${counterSale.entry?.localId}` : 'Cart'}
        </Typography>
        <Stack direction="row" alignItems="center" spacing={2}>
          {!readOnly && (
            <Button onClick={removeAllParts}>
              {counterSale.pricing?.payments?.length === 0
                ? 'Clear Cart'
                : 'Start New Transaction'}
            </Button>
          )}
        </Stack>
      </Stack>
      <Typography variant="caption">
        {`${numItemsInCart} ${
          numItemsInCart === 1 ? 'item' : 'items'
        } in the basket`}
      </Typography>
      <Divider variant="middle" />
      <Stack direction="column" spacing={2} paddingY={1}>
        <CounterSaleCartInventory
          counterSale={counterSale}
          readOnly={readOnly}
        />
        <CounterSaleCartProduct counterSale={counterSale} readOnly={readOnly} />
      </Stack>
      <Stack direction="column" spacing={2}>
        <Stack
          direction="row"
          justifyContent="space-between"
          sx={{ width: '100%' }}>
          <Typography variant="body1">Subtotal:</Typography>
          <Typography variant="body1">
            {formatCurrency(counterSale.pricing?.partsRetailPrice || 0, true)}
          </Typography>
        </Stack>
        <CounterSaleCartDiscount
          counterSale={counterSale}
          readOnly={readOnly}
        />
        <Stack
          direction="row"
          justifyContent="space-between"
          sx={{ width: '100%' }}>
          <Typography variant="body1">Tax:</Typography>
          <Typography variant="body1">
            {formatCurrency(counterSale.pricing?.taxes || 0, true)}
          </Typography>
        </Stack>
        <Divider />
        <Stack
          direction="row"
          justifyContent="space-between"
          sx={{ width: '100%' }}>
          <Typography variant="h6">Total:</Typography>
          <Typography variant="h2">
            {formatCurrency(
              counterSale.pricing?.totalBeforeSurcharge || 0,
              true
            )}
          </Typography>
        </Stack>
        {counterSale.pricing?.total !==
          counterSale.pricing?.totalBeforeSurcharge && (
          <Stack
            direction="row"
            justifyContent="space-between"
            sx={{ width: '100%' }}>
            <Typography variant="h6">
              Total With Credit Card Surcharge:
            </Typography>
            <Typography variant="h2">
              {formatCurrency(counterSale.pricing?.total || 0, true)}
            </Typography>
          </Stack>
        )}
        {showCheckout && (
          <>
            <Button
              onClick={handleNext}
              variant="contained"
              disabled={numItemsInCart === 0}>
              Checkout
            </Button>
          </>
        )}
        {showReceipt && (
          <Button
            onClick={openReceipt}
            variant="contained"
            startIcon={<PrintIcon />}>
            Print Receipt
          </Button>
        )}
        {showCustomer && (
          <Box padding={2} minWidth={200}>
            <CustomerLookupInput
              customer={counterSale.pricing?.customerEntry}
              handleCustomerChange={handleCustomerChange}
            />
          </Box>
        )}
      </Stack>
    </>
  )
}

export default CounterSaleCart
